import React from "react";
import { HeaderSection } from "@/components/mentor/minhas-mentorias/HeaderSection";
import { TypeSelectionModal } from "@/components/mentor/minhas-mentorias/TypeSelectionModal";
import { OfferingDialogs } from "@/components/mentor/minhas-mentorias/OfferingDialogs";
import { ContentDialogs } from "@/components/mentor/meus-conteudos/ContentDialogs";
import { UnifiedServiceCard } from "@/components/mentor/minhas-mentorias/UnifiedServiceCard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useIntegratedServices } from "@/hooks/useIntegratedServices";
import { MentorshipFormData } from "@/components/mentor/MentorshipOfferingForm";
import { DigitalContentFormData } from "@/types/digitalContent";

const MinhasMentorias = () => {
  const {
    services,
    loading,
    isTypeSelectionOpen,
    setIsTypeSelectionOpen,
    isMentoriaDialogOpen,
    setIsMentoriaDialogOpen,
    isConteudoDialogOpen,
    setIsConteudoDialogOpen,
    isEditMentoriaOpen,
    setIsEditMentoriaOpen,
    isEditConteudoOpen,
    setIsEditConteudoOpen,
    editingMentoria,
    editingConteudo,
    handleCreateClick,
    handleSelectMentoria,
    handleSelectConteudo,
    handleCreateMentoria,
    handleCreateConteudo,
    handleDeleteService,
    handleEditService,
    handleUpdateMentoria,
    handleUpdateConteudo,
    handleToggleActive
  } = useIntegratedServices();

  // Separar serviços por tipo
  const mentorias = services.filter(service => service.type === 'mentoria');
  const conteudos = services.filter(service => service.type === 'conteudo');

  const EmptyStateComponent = ({ type, icon, title, description }: {
    type: string;
    icon: string;
    title: string;
    description: string;
  }) => (
    <div className="text-center py-8 bg-[#1A2233] rounded-lg border border-[#222A35]">
      <div className="max-w-md mx-auto">
        <div className="text-4xl mb-3">{icon}</div>
        <h4 className="text-lg font-medium text-white mb-2">{title}</h4>
        <p className="text-gray-400 text-sm mb-4">{description}</p>
        <button
          onClick={handleCreateClick}
          className="bg-[#00B6F0] text-black px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#00A3D9] transition-colors"
        >
          Criar {type}
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <HeaderSection onCreateClick={handleCreateClick} />
      
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00B6F0] mx-auto mb-4"></div>
          <p className="text-gray-400">Carregando seus serviços...</p>
        </div>
      ) : (
        <>
          {/* Bloco de Mentorias */}
          <Card className="bg-[#1E293B] border-[#2A3749]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <span className="text-2xl">🎯</span>
                Minhas Mentorias
                <span className="text-sm bg-[#00B6F0] text-black px-2 py-1 rounded-full">
                  {mentorias.length}
                </span>
              </CardTitle>
              <CardDescription className="text-gray-400">
                Sessões de mentoria ao vivo com agendamento personalizado
              </CardDescription>
            </CardHeader>
            <CardContent>
              {mentorias.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {mentorias.map(service => (
                    <UnifiedServiceCard
                      key={`${service.type}-${service.id}`}
                      service={service}
                      onEdit={handleEditService}
                      onDelete={handleDeleteService}
                      onToggleActive={handleToggleActive}
                    />
                  ))}
                </div>
              ) : (
                <EmptyStateComponent
                  type="Mentoria"
                  icon="🎯"
                  title="Nenhuma mentoria criada"
                  description="Crie mentorias personalizadas para compartilhar seu conhecimento"
                />
              )}
            </CardContent>
          </Card>

          {/* Bloco de Conteúdos Digitais */}
          <Card className="bg-[#1E293B] border-[#2A3749]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <span className="text-2xl">📚</span>
                Meus Conteúdos Digitais
                <span className="text-sm bg-green-500 text-black px-2 py-1 rounded-full">
                  {conteudos.length}
                </span>
              </CardTitle>
              <CardDescription className="text-gray-400">
                Materiais digitais com acesso vitalício após compra
                <span className="block text-xs text-green-400 mt-1">
                  ✓ Acesso permanente mesmo se inativado ou excluído
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              {conteudos.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {conteudos.map(service => (
                    <UnifiedServiceCard
                      key={`${service.type}-${service.id}`}
                      service={service}
                      onEdit={handleEditService}
                      onDelete={handleDeleteService}
                      onToggleActive={handleToggleActive}
                    />
                  ))}
                </div>
              ) : (
                <EmptyStateComponent
                  type="Conteúdo"
                  icon="📚"
                  title="Nenhum conteúdo criado"
                  description="Crie conteúdos digitais como PDFs, vídeos e materiais exclusivos"
                />
              )}
            </CardContent>
          </Card>

          {/* Estado vazio geral - só aparece se não tiver nenhum serviço */}
          {services.length === 0 && (
            <Card className="bg-[#1E293B] border-[#2A3749]">
              <CardContent className="pt-6">
                <div className="text-center py-12">
                  <div className="max-w-md mx-auto">
                    <div className="text-6xl mb-4">🚀</div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Comece sua jornada como mentor
                    </h3>
                    <p className="text-gray-400 mb-6">
                      Crie sua primeira mentoria ou conteúdo digital para compartilhar conhecimento e gerar renda
                    </p>
                    <button
                      onClick={handleCreateClick}
                      className="bg-[#00B6F0] text-black px-6 py-3 rounded-lg font-medium hover:bg-[#00A3D9] transition-colors"
                    >
                      Criar Primeiro Serviço
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Modal de Seleção de Tipo */}
      <TypeSelectionModal
        isOpen={isTypeSelectionOpen}
        onClose={() => setIsTypeSelectionOpen(false)}
        onSelectMentoria={handleSelectMentoria}
        onSelectConteudo={handleSelectConteudo}
      />

      {/* Modal de Criação de Mentoria */}
      <OfferingDialogs
        isCreateDialogOpen={isMentoriaDialogOpen}
        setIsCreateDialogOpen={setIsMentoriaDialogOpen}
        isEditDialogOpen={isEditMentoriaOpen}
        setIsEditDialogOpen={setIsEditMentoriaOpen}
        editingOffering={editingMentoria}
        handleCreateOffering={handleCreateMentoria}
        handleUpdateOffering={handleUpdateMentoria}
      />

      {/* Modal de Criação/Edição de Conteúdo */}
      <ContentDialogs
        isCreateOpen={isConteudoDialogOpen}
        isEditOpen={isEditConteudoOpen}
        onCreateClose={() => setIsConteudoDialogOpen(false)}
        onEditClose={() => setIsEditConteudoOpen(false)}
        onSubmit={isEditConteudoOpen ? handleUpdateConteudo : handleCreateConteudo}
        isLoading={false}
        editData={editingConteudo ? ({
          title: editingConteudo.title,
          description: editingConteudo.description,
          content_type: editingConteudo.content_type as any,
          file_url: editingConteudo.file_url,
          price_cents: editingConteudo.price_cents
        } as any) : undefined}
      />
    </div>
  );
};

export default MinhasMentorias;
